<template>
    <div id="org_settings_connections_systems" class="d-flex flex-column gap-3">
        <div class="row">
            <div class="col-lg-8 col-12">
                <h3 class="m-2">Your Connections ({{ filteredConnections.length }})</h3>
            </div>
            <div class="col-lg-4 col-12">
                <div class="d-flex justify-content-end gap-2">
                    <div class="dropdown position-relative filters-dropdown">
                        <button class="btn btn-secondary dropdown-toggle dropdown-toggle-no-btn" data-bs-toggle="dropdown" data-bs-auto-close="outside" aria-expanded="false">
                            <img class="icon me-2" src="/img/icons/avid-icons/filter-funnel-01.svg" alt="">Filters
                            <span v-if="activeFilterCount > 0" class="position-absolute top-0 start-100 translate-middle badge rounded-pill badge-blue">
                                {{ activeFilterCount }}
                            </span>
                        </button>
                        <div class="dropdown-menu dropdown-menu-primary p-3">
                            <div v-if="activeFilterCount > 0" class="mb-3">
                                <a href="#" @click.prevent="clearAllFilters" class="text-primary">
                                    <i class="fa fa-times me-1"></i>Clear All Filters
                                </a>
                                <hr class="my-2">
                            </div>
                            <label class="form-label">Status:</label>
                            <select class="form-select mb-3" v-model="filters.status" @change="handleFilterChange" style="min-width: 180px;" aria-label="Status:">
                                <option value="">All Statuses</option>
                                <option value="active">Active</option>
                                <option value="syncing">Syncing</option>
                                <option value="error">Error</option>
                                <option value="initializing">Initializing</option>
                            </select>
                            <label class="form-label">Capability:</label>
                            <select class="form-select" v-model="filters.capability" @change="handleFilterChange" style="min-width: 180px;" aria-label="Capability:">
                                <option value="">All Capabilities</option>
                                <option value="reporting">Reporting</option>
                                <option value="audiences">Audiences</option>
                                <option value="playbooks">Playbooks</option>
                            </select>
                        </div>
                    </div>
                    <div v-if="!canEdit('connections')" data-bs-toggle="tooltip" title="You do not have privileges to complete this action">
                        <button class="btn btn-secondary" :disabled="true">
                            <img class="icon" src="/img/icons/avid-icons/plus.svg" alt="Add">
                            Add System
                        </button>
                    </div>
                    <button v-else class="btn btn-primary" @click.prevent="$emit('addSystem')">
                        <img class="icon" src="/img/icons/avid-icons/plus.svg" alt="Add">
                        Add System
                    </button>  
                </div>
            </div>
        </div>
        <div v-if="filteredConnections.length === 0" class="text-center py-5">
            <h5>No connections found</h5>
            <p v-if="activeFilterCount > 0">Try adjusting your filters to see more results.</p>
            <p v-else>There are no connections configured yet.</p>
        </div>
        <div v-else id="system-connections-grid">
            <div v-for="(conn, index) in filteredConnections" :key="conn.id" class="card content-card system-connection-card h-100">
                <div class="card-body d-flex flex-column">
                    <div class="system-connection-card__header content-card-header">
                        <div class="system-connection-card__header__left d-flex align-items-center flex-grow-1 min-width-0">
                            <img :src="conn.integration.logo_url" :alt="conn.integration.display_name" class="avatar rounded-circle avatar-sm me-2 bg-white flex-shrink-0">
                            <h3 class="mb-0 text-truncate" 
                                :title="conn.integration.display_name" 
                                data-bs-toggle="tooltip" 
                                data-bs-placement="top">
                                {{ conn.integration.display_name }}
                            </h3>
                        </div>
                        
                        <div class="system-connection-card__header__right d-flex align-items-center gap-2 flex-shrink-0">
                            <div class="system-connection-card__header__status">
                                <span class="badge badge-secondary d-inline-block" v-if="!conn.sync_error_msg && !conn.last_synced_at && !conn.initial_sync_at">Initializing</span>
                                <span class="badge bg-red bg-icon-danger d-inline-block" v-else-if="conn.sync_error_msg || getLastSyncStatus(conn.id) === 'failed'">Error</span>
                                <span class="badge bg-gray bg-icon-progress d-inline-block" v-else-if="conn.active_jobs?.length > 0 || isSyncBusy(conn.id)">Syncing</span>
                                <span class="badge bg-green bg-icon-success d-inline-block" v-else>Active</span>
                            </div>

                            <div class="system-connection-card__header__actions" v-if="(canSync(conn) || canDelete(conn)) && canEdit('connections')">
                                <button class="btn btn-white btn-icon-only-md" data-bs-toggle="dropdown" data-bs-auto-close="true" aria-expanded="false" aria-label="Actions">
                                    <img class="icon" src="/img/icons/avid-icons/dots-vertical.svg" alt="Menu" data-bs-toggle="tooltip" title="Actions">
                                </button>
                                <ul class="dropdown-menu dropdown-menu-primary">
                                    <li><a class="dropdown-item" href="#" @click.prevent="updateConnection(conn)">
                                        <img class="icon me-2" src="/img/icons/avid-icons/key.svg" alt=""> Update Credentials
                                    </a></li>
            
                                    <li v-if="canSync(conn)"><a class="dropdown-item" href="#" @click.prevent="syncConnection(conn)" :disabled="disable_actions">
                                        <img class="icon me-2" src="/img/icons/avid-icons/sync.svg" alt=""> Sync Now</a>
                                    </li>
                    
                                    <li v-if="canDelete(conn)"><a href="#" class="dropdown-item text-danger" :disabled="disable_actions" @click.prevent="deleteConnection(conn.id)">
                                        <img class="icon red-icon me-2" src="/img/icons/avid-icons/trash.svg" alt=""> Delete</a>
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </div>
                    
                    <div class="system-connection-card__content content-card-body flex-grow-1 d-flex flex-column">
                        <div class="flex-grow-1">
                            <div class="d-flex flex-row align-items-center">
                                <div class="d-flex flex-column">
                                    <p>{{ conn.name }}</p>
                                </div>
                            </div>

                            <div class="d-flex flex-row align-items-center mt-2">
                                <div class="d-flex flex-column">
                                    <small v-if="conn.sync_error_msg || getLastSyncStatus(conn.id) === 'failed'"><em
                                            v-html="(conn.sync_error_msg || getLastSyncErrorMessage(conn.id)) + (conn.error_reference_id ? ' (Reference: ' + conn.error_reference_id + ')' : '')"></em></small>
                                    <small v-else-if="conn.active_jobs?.length > 0">Started At: {{ datetime(conn.last_synced_at) }} | {{ number_with_zero(conn.active_jobs[0].rowsSynced) }} Rows Synced</small>
                                    <small v-else-if="conn.last_synced_at == null">Created on: {{ date(conn.created_at) }}</small>
                                    <small v-else>Last Synced: {{ date(conn.last_synced_at) }} | Created on: {{ date(conn.created_at) }}</small>
                                </div>
                            </div>         

                            <div class="connection-capabilities-badges mt-2 d-flex flex-wrap gap-2">
                                <span class="badge capability-badge capability-badge-reporting">
                                    <img class="icon me-1" src="/img/icons/nav-icons/Insights.svg" alt="Reporting">
                                    Reporting
                                </span>
                                <span v-if="hasAudiencesCapability(conn.integration)" class="badge capability-badge capability-badge-audiences">
                                    <img class="icon me-1" src="/img/icons/nav-icons/Pathways.svg" alt="Audiences">
                                    Audiences
                                </span>
                                <span v-if="hasPlaybooksCapability(conn.integration)" class="badge capability-badge capability-badge-playbooks">
                                    <img class="icon me-1" src="/img/icons/nav-icons/Playbooks.svg" alt="Playbooks">
                                    Playbooks
                                </span>
                            </div>
                        </div>
                        
                        <div class="w-100 d-flex flex-row align-items-center mt-auto">
                            <div class="d-flex flex-column flex-grow-1" @click="viewConnection(conn)">
                                <button class="btn btn-white w-100 mt-4">View Connection</button>
                            </div>
                        </div>
                    </div>

                </div>
            </div>
        </div>

        <div class="row my-3" id="need-more-insights" v-if="canEdit('connections')">
            <div class="d-inline-flex justify-content-center align-items-center">
                <p>Need more insights?
                <a href="#" class="text-decoration-underline" @click.prevent="$emit('addSystem')">Add another connection</a></p>
            </div>
        </div>


    </div>
</template>

<script>
import { defineComponent } from 'vue';
import permissionsMixin from '../../mixins/permissions'

export default defineComponent({

    mixins: [permissionsMixin],
    props: ['client', 'integrations', 'connections'],
    data() {
        return {
            local_connections: [],
            disable_actions: false,
            census_connections: [],
            filters: {
                status: '',
                capability: ''
            }
        };
    },
    beforeMount() {
        this.local_connections = this.connections;
        this.census_connections = this.connections.filter(conn => conn.census_connections && conn.census_connections.length > 0);
        this.listenForUpdates();
    },
    mounted() {
        // Initialize tooltips
        this.initializeTooltips();
        
        // Check if URL contains a connection ID hash
        const hash = window.location.hash;
        if (hash && hash.startsWith('#conn_')) {
            const connectionId = hash.replace('#conn_', '');
            const connection = this.local_connections.find(conn => conn.id == connectionId);
            
            if (connection) {
                this.updateConnection(connection);
            }
        }

        // Check for modal querystring parameter
        this.checkModalQueryParam();
    },
    computed: {
        filteredConnections() {
            let filtered = this.local_connections;

            // Filter by status
            if (this.filters.status) {
                filtered = filtered.filter(conn => {
                    if (this.filters.status === 'active') {
                        // Active: not initializing, not error, not syncing
                        return !conn.sync_error_msg && 
                               (conn.last_synced_at || conn.initial_sync_at) && 
                               !conn.active_jobs?.length && 
                               !this.isSyncBusy(conn.id) && 
                               this.getLastSyncStatus(conn.id) !== 'failed';
                    } else if (this.filters.status === 'syncing') {
                        return conn.active_jobs?.length > 0 || this.isSyncBusy(conn.id);
                    } else if (this.filters.status === 'error') {
                        return conn.sync_error_msg || this.getLastSyncStatus(conn.id) === 'failed';
                    } else if (this.filters.status === 'initializing') {
                        return !conn.sync_error_msg && !conn.last_synced_at && !conn.initial_sync_at;
                    }
                    return true;
                });
            }

            // Filter by capability
            if (this.filters.capability) {
                filtered = filtered.filter(conn => {
                    if (this.filters.capability === 'reporting') {
                        return true; // All connections have reporting capability
                    } else if (this.filters.capability === 'audiences') {
                        return this.hasAudiencesCapability(conn.integration);
                    } else if (this.filters.capability === 'playbooks') {
                        return this.hasPlaybooksCapability(conn.integration);
                    }
                    return true;
                });
            }

            return filtered;
        },
        activeFilterCount() {
            let count = 0;
            if (this.filters.status) count++;
            if (this.filters.capability) count++;
            return count;
        }
    },
    watch: {
        connections: {
            handler(newVal, oldVal) {
                this.local_connections = this.connections;
                this.$forceUpdate();
                // Reinitialize tooltips after connections update
                this.$nextTick(() => {
                    this.initializeTooltips();
                });
            },
            deep: true
        }
    },
    methods: {
        initializeTooltips() {
            // Initialize tooltips
            const tooltipTriggerList = document.querySelectorAll('[data-bs-toggle="tooltip"]');
            const tooltipList = [...tooltipTriggerList].map(tooltipTriggerEl => new window.bootstrap.Tooltip(tooltipTriggerEl));

            // Add event listeners to hide tooltips on click and mouseleave
            tooltipTriggerList.forEach((trigger, index) => {
                trigger.addEventListener('click', () => {
                    tooltipList[index].hide();
                });
                trigger.addEventListener('mouseleave', () => {
                    tooltipList[index].hide();
                });
            });
        },
        listenForUpdates() {
            window.Echo.private(`client.${this.client.id}`)
                .listen('SyncJobUpdated', (e) => {
                    // Find the connection that needs to be updated
                    const connection = this.local_connections.find(
                        conn => conn.id === e.connection_id
                    );
                    
                    if (connection) {
                        // Update the connection's active jobs if it's currently syncing
                        const busyStatuses = ['queued', 'pending', 'running', 'working'];
                        if (busyStatuses.includes(e.status)) {
                            connection.active_jobs = [{
                                rowsSynced: e.rowsSynced || 0
                            }];
                            connection.last_synced_at = e.started_at;
                        } else {
                            connection.active_jobs = [];
                            connection.last_synced_at = e.finished_at;
                        }
                        
                        // Update error message if there is one
                        connection.sync_error_msg = e.error_msg;
                        
                        this.$forceUpdate();
                    }
                });
        },
        canSync(conn) {
            //The SFTPs
            if(conn.integration_id == '7dbce608-4906-4606-977c-32ab1259b2e6' || conn.integration_id == '397b5b86-e954-495f-8fdb-513885f53251')
                return false
            //File upload or File Forwarding
            if(conn.integration_id == '71b0c3b0-8390-49ec-9984-1c04b7384d38' || conn.integration_id == '323ce508-d21b-44ec-8064-5623e498ad13')
                return false;
            //If it is still unconfigured...
            if(conn.system_account_id == null)
                return false;

            return conn.active_jobs == null || conn.active_jobs.length == 0
        },
        canDelete(conn) {
            //Can't delete the File Upload connection
            return conn.integration_id != '71b0c3b0-8390-49ec-9984-1c04b7384d38';
        },
        viewConnection(conn) {
            this.$emit('viewConnection', conn)
        },
        updateConnection(conn) {
            this.$emit('updateConnection', conn)
        },
        deleteConnection(conn_id) {
            Swal.fire({
                title: "Are you sure you want to delete this connection?",
                text: "Deleting the connection will remove the connection, all associated data, and any Pathways that utilize that data.",
                icon: "warning",
                showCancelButton: true,
                confirmButtonText: "Yes, delete",
                customClass: {
                    popup: 'swal-warning'
                },
            }).then((result) => {
                if (result.isConfirmed) {
                    this.disable_actions = true;

                    window.axios.delete('/api/connection/' + conn_id).then(response => {
                        //Find the connection in the local_connections
                        let index = this.local_connections.findIndex(x => x.id === conn_id);
                        //Remove it
                        this.local_connections.splice(index, 1);
                        this.disable_actions = false;
                        this.$forceUpdate();

                        Swal.fire({
                            title: "Deleted!",
                            text: "The connection has been removed.",
                            icon: "success",
                            customClass: {
                                popup: 'swal-success'
                            },
                        });
                    }).catch(error => {
                        this.disable_actions = false;
                        // Handle errors here
                        Swal.fire({
                            title: "Error!",
                            text: error.response.data.message,
                            icon: "error",
                            customClass: {
                                popup: 'swal-error'
                            },
                        });
                    });
                }
            });
        },
        syncConnection(conn) {
            this.disable_actions = true;
            window.axios.get('/api/connection/' + conn.id + '/refresh').then(response => {
                this.disable_actions = false;
                //This will change the status
                conn.active_jobs = ["something"];
                conn.sync_error_msg = null; // Clear error message when starting a sync
                this.$forceUpdate();

                Swal.fire({
                    title: "Success!",
                    text: "The connection will begin refreshing momentarily.",
                    icon: "success",
                    customClass: {
                        popup: 'swal-success'
                    },
                });
            }).catch(error => {
                this.disable_actions = false;
                // Handle errors here
                Swal.fire({
                    title: "Error!",
                    text: error.response.data.message,
                    icon: "error",
                    customClass: {
                        popup: 'swal-error'
                    },
                });
            });
        },
        getLastSync(conn_id) {
            let lastSync = null;
            let lastFailedSync = null;
            let mostRecentDate = null;

            //Get all syncs for this connection
            for(var i = 0; i < this.census_connections.length; i++) {
                if(this.census_connections[i].id == conn_id && this.census_connections[i].census_connections) {
                    for(var j = 0; j < this.census_connections[i].census_connections.length; j++) {
                        const census_conn = this.census_connections[i].census_connections[j];
                        if(census_conn.syncs && census_conn.syncs.length > 0) {
                            // Check each sync
                            for(var k = 0; k < census_conn.syncs.length; k++) {
                                const sync = census_conn.syncs[k];
                                
                                // If this sync failed, keep track of it
                                if(sync.last_sync_status === 'failed') {
                                    if(!lastFailedSync || new Date(sync.last_synced_at) > new Date(lastFailedSync.last_synced_at)) {
                                        lastFailedSync = sync;
                                    }
                                }
                                
                                // Keep track of most recent sync
                                if(!mostRecentDate || new Date(sync.last_synced_at) > mostRecentDate) {
                                    mostRecentDate = new Date(sync.last_synced_at);
                                    lastSync = sync;
                                }
                            }
                        }
                    }
                }
            }

            // Return failed sync if one exists, otherwise return most recent
            return lastFailedSync || lastSync;
        },
        getLastSyncStatus(conn_id) {
            var last_sync = this.getLastSync(conn_id);
            if(last_sync != null)
                return last_sync.last_sync_status;
            return null;
        },
        getLastSyncErrorMessage(conn_id) {
            var last_sync = this.getLastSync(conn_id);
            if(last_sync != null)
                return '<strong>Pathway Error:</strong> ' + last_sync.last_sync_error_message;
            return null;
        },
        isSyncBusy(conn_id) {
            const busyStatuses = ['queued', 'pending', 'running', 'working'];
            for(var i = 0; i < this.census_connections.length; i++) {
                if(this.census_connections[i].id == conn_id && this.census_connections[i].census_connections) {
                    for(var j = 0; j < this.census_connections[i].census_connections.length; j++) {
                        const census_conn = this.census_connections[i].census_connections[j];
                        if(census_conn.syncs && census_conn.syncs.length > 0) {
                            const lastSync = census_conn.syncs[census_conn.syncs.length - 1];
                            if(busyStatuses.includes(lastSync.last_sync_status)) {
                                return true;
                            }
                        }
                    }
                }
            }
            return false;
        },

        checkModalQueryParam() {
            const urlParams = new URLSearchParams(window.location.search);
            const modalParam = urlParams.get('modal');

            if (modalParam) {
                // Find the connection by ID
                const connection = this.local_connections.find(conn => conn.id == modalParam);
                
                if (connection) {
                    // Remove the parameter from the URL
                    urlParams.delete('modal');
                    const newUrl = window.location.pathname + (urlParams.toString() ? '?' + urlParams.toString() : '') + window.location.hash;
                    window.history.replaceState({}, document.title, newUrl);

                    // Open the modal for this connection
                    this.updateConnection(connection);
                    

                }
            }
        },
        hasAudiencesCapability(integration) {
            // Audiences: has a value in census_connection_type column
            if (!integration) return false;
            return integration.census_connection_type && integration.census_connection_type.trim() !== '';
        },
        hasPlaybooksCapability(integration) {
            // Playbooks: playbook_deployment is true
            if (!integration) return false;
            return integration.playbook_deployment === true || integration.playbook_deployment === 1;
        },
        handleFilterChange() {
            // Force update to refresh filtered connections
            this.$forceUpdate();
        },
        clearAllFilters() {
            this.filters.status = '';
            this.filters.capability = '';
            this.handleFilterChange();
        }
    }
});
</script>
